/* Public sign-in screen: two-step email OTP (send code → verify) plus Google.
 * Login is client-side via Supabase; on success the session lands in context and
 * we return the user to wherever they came from (defaulting to home). */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Logo } from "../../assets/Logo";
import { useAuth } from "./AuthProvider";
import "./auth.css";

/* Only allow returning to an internal path — never an attacker-supplied absolute
   URL — to avoid an open redirect. */
function safeInternalPath(value: unknown): string {
  return typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//")
    ? value
    : "/";
}

type Step = "email" | "code";

export function LoginPage(): React.ReactElement {
  const { session, signInWithOtp, verifyOtp, signInWithGoogle } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = safeInternalPath(
    (location.state as { from?: unknown } | null)?.from,
  );

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  // Already signed in (e.g. navigated here manually) → leave.
  useEffect(() => {
    if (session) navigate(dest, { replace: true });
  }, [session, dest, navigate]);

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error } = await signInWithOtp(email.trim());
    setBusy(false);
    if (error) setError(error.message);
    else setStep("code");
  }

  async function resendCode() {
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error } = await signInWithOtp(email.trim());
    setBusy(false);
    if (error) setError(error.message);
    else setNotice("New code sent.");
  }

  async function doVerify(value: string) {
    if (busy || value.length < 6) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    const { error } = await verifyOtp(email.trim(), value);
    setBusy(false);
    if (error) setError(error.message);
    // On success the auth listener sets the session; the effect above navigates.
  }

  function verify(e: React.FormEvent) {
    e.preventDefault();
    void doVerify(code);
  }

  /* Auto-submit the moment the 6th digit lands — one less tap, and the
     Continue button stays as the retry path after a failed attempt. */
  function handleCodeChange(value: string) {
    setCode(value);
    if (error) setError(null);
    if (value.length === 6) void doVerify(value);
  }

  async function google() {
    setBusy(true);
    setError(null);
    const { error } = await signInWithGoogle();
    // On success the browser redirects to Google, so we only land here on error.
    if (error) {
      setError(error.message);
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-brand">
          <Logo size={34} />
          Grubwick
        </div>

        {step === "email" ? (
          <>
            <h1 className="auth-title">Sign in or create an account</h1>

            <div className="auth-main">
              {error && <div className="auth-error">{error}</div>}

              <button
                className="auth-btn auth-btn-google"
                type="button"
                onClick={google}
                disabled={busy}
              >
                <GoogleMark />
                Continue with Google
              </button>

              <div className="auth-divider">or</div>

              <form className="auth-form" onSubmit={sendCode}>
                <input
                  className="auth-input"
                  type="email"
                  aria-label="Email"
                  required
                  autoComplete="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                />
                <button
                  className="auth-btn auth-btn-primary"
                  type="submit"
                  disabled={busy}
                >
                  {busy ? "Sending code…" : "Continue with email"}
                </button>
              </form>
            </div>
          </>
        ) : (
          <>
            <h1 className="auth-title">Check your email</h1>
            <p className="auth-sub">
              We sent a 6-digit code to <strong>{email}</strong>
            </p>

            <div className="auth-main">
              {error && <div className="auth-error">{error}</div>}
              {notice && <div className="auth-notice">{notice}</div>}

              <form className="auth-form" onSubmit={verify}>
                <CodeInput value={code} onChange={handleCodeChange} />
                <button
                  className="auth-btn auth-btn-primary"
                  type="submit"
                  disabled={busy || code.trim().length < 6}
                >
                  {busy ? "Signing in…" : "Continue"}
                </button>
              </form>

              <p className="auth-alt">
                <button
                  className="auth-linkbtn"
                  type="button"
                  onClick={resendCode}
                  disabled={busy}
                >
                  Resend code
                </button>
                <span className="auth-alt-sep" aria-hidden="true">
                  ·
                </span>
                <button
                  className="auth-linkbtn"
                  type="button"
                  onClick={() => {
                    setStep("email");
                    setCode("");
                    setError(null);
                    setNotice(null);
                  }}
                >
                  Use a different email
                </button>
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const CODE_LENGTH = 6;

/* Segmented one-time-code entry. The six boxes are purely presentational — a
   single invisible <input> stretched across them holds the real value, so
   paste, backspace, OS code autofill (autocomplete="one-time-code") and screen
   readers all behave like a normal text field. */
function CodeInput(props: {
  value: string;
  onChange: (value: string) => void;
}): React.ReactElement {
  const [focused, setFocused] = useState(false);
  const activeIndex = Math.min(props.value.length, CODE_LENGTH - 1);

  /* Typing should always append at the end; a caret parked mid-value in the
     invisible input would silently insert digits out of order. */
  function caretToEnd(input: HTMLInputElement) {
    input.setSelectionRange(props.value.length, props.value.length);
  }

  return (
    <div className="otp">
      <input
        className="otp-field"
        type="text"
        inputMode="numeric"
        pattern="\d*"
        aria-label="6-digit code"
        autoFocus
        autoComplete="one-time-code"
        value={props.value}
        onChange={(ev) =>
          props.onChange(
            ev.target.value.replace(/\D/g, "").slice(0, CODE_LENGTH),
          )
        }
        onFocus={(ev) => {
          setFocused(true);
          caretToEnd(ev.target);
        }}
        onBlur={() => setFocused(false)}
        onSelect={(ev) => caretToEnd(ev.currentTarget)}
      />
      {Array.from({ length: CODE_LENGTH }, (_, i) => (
        <div
          key={i}
          aria-hidden="true"
          className={
            "otp-box" +
            (focused && i === activeIndex && props.value.length < CODE_LENGTH
              ? " otp-box-active"
              : "")
          }
        >
          {props.value[i] ??
            (focused && i === activeIndex ? (
              <span className="otp-caret" />
            ) : (
              ""
            ))}
        </div>
      ))}
    </div>
  );
}

/* Google "G" — classic flat four-color version, kept inline (single-use,
   brand-colored, not part of the app icon set). */
function GoogleMark(): React.ReactElement {
  return (
    <svg width={16} height={16} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

/* Google gradient "super G" — verbatim from Google's official sign-in asset bundle
   (their branding guidelines forbid modifying the logo, hence the mask/blur
   structure). Disabled for now: we prefer the classic flat G above. To switch
   back, delete GoogleMark above and un-comment this block (GoogleGradientMark +
   GBlur), then rename GoogleGradientMark → GoogleMark. Note the conic gradient
   relies on <foreignObject>, which only renders when the SVG is inline in the
   document — don't move it to a standalone .svg file.

function GoogleGradientMark(): React.ReactElement {
  return (
    <svg
      width={20}
      height={20}
      viewBox="10 10 20 20"
      fill="none"
      aria-hidden="true"
    >
      <mask
        id="gw-g-mask"
        style={{ maskType: "alpha" }}
        maskUnits="userSpaceOnUse"
        x={10}
        y={10}
        width={20}
        height={20}
      >
        <path
          d="M29.3987 18.1814H19.9849V22.0445H25.3598C25.1286 23.294 24.4294 24.3596 23.3676 25.0712C22.4746 25.6716 21.3266 26.0211 19.9849 26.0211C17.3864 26.0211 15.1823 24.2666 14.3947 21.9004C14.1952 21.2989 14.0853 20.6599 14.0853 19.9983C14.0853 19.3367 14.1952 18.6966 14.3947 18.0962C15.1823 15.7311 17.3864 13.9755 19.9849 13.9755C21.4524 13.9755 22.767 14.4816 23.8039 15.4713L26.6653 12.6057C24.936 10.9908 22.6786 10 19.9849 10C16.0832 10 12.705 12.2414 11.0618 15.5076C10.383 16.8592 10 18.3834 10 19.9994C10 21.6155 10.383 23.1396 11.0618 24.4913C12.705 27.7597 16.0832 30 19.9849 30C22.6797 30 24.9485 29.1137 26.6018 27.5861C28.4887 25.8452 29.5732 23.2702 29.5732 20.2275C29.5732 19.5182 29.5131 18.835 29.3987 18.1825V18.1814Z"
          fill="#E94FFF"
        />
      </mask>
      <g mask="url(#gw-g-mask)">
        <g filter="url(#gw-g-blur0)">
          <g clipPath="url(#gw-g-clip)">
            <g transform="matrix(0.00804129 -0.00805186 0.00804128 0.00805186 19.6819 19.7927)">
              <foreignObject
                x={-2105.64}
                y={-2105.64}
                width={4211.29}
                height={4211.29}
              >
                <div
                  style={{
                    background:
                      "conic-gradient(from 90deg,rgba(255, 70, 65, 1) 0deg,rgba(255, 70, 65, 1) 4.14555deg,rgba(49, 134, 255, 1) 39.154deg,rgba(49, 134, 255, 1) 72.0044deg,rgba(0, 165, 183, 1) 96.7463deg,rgba(14, 188, 95, 1) 120.897deg,rgba(14, 188, 95, 1) 154.722deg,rgba(108, 196, 0, 1) 179.136deg,rgba(255, 204, 0, 1) 203.588deg,rgba(255, 211, 20, 1) 226.915deg,rgba(255, 204, 0, 1) 251.688deg,rgba(255, 106, 43, 1) 273.129deg,rgba(253, 70, 65, 1) 289.305deg,rgba(255, 70, 65, 1) 359.593deg,rgba(255, 70, 65, 1) 360deg)",
                    height: "100%",
                    width: "100%",
                    opacity: 1,
                  }}
                />
              </foreignObject>
            </g>
          </g>
        </g>
        <g filter="url(#gw-g-blur1)">
          <ellipse
            cx={20.0496}
            cy={20.2413}
            rx={5.39634}
            ry={2.83537}
            transform="rotate(24.4473 20.0496 20.2413)"
            fill="#3186FF"
          />
        </g>
        <g filter="url(#gw-g-blur2)">
          <ellipse cx={33.3538} cy={18.2155} rx={7.43918} ry={3.09357} fill="#3186FF" />
        </g>
        <g filter="url(#gw-g-blur3)">
          <ellipse cx={25.2744} cy={16.2195} rx={7.40854} ry={2.37805} fill="#FF4641" />
        </g>
        <g filter="url(#gw-g-blur4)">
          <ellipse cx={29.5427} cy={12.9268} rx={7.40854} ry={2.37805} fill="#FF5B8B" />
        </g>
        <g filter="url(#gw-g-blur5)">
          <ellipse cx={24.4817} cy={19.878} rx={8.5061} ry={3.10976} fill="#3186FF" />
        </g>
        <g filter="url(#gw-g-blur6)">
          <ellipse
            cx={25.1842}
            cy={14.0197}
            rx={4.53882}
            ry={2.37805}
            transform="rotate(-28.6599 25.1842 14.0197)"
            fill="#FF4641"
          />
        </g>
      </g>
      <defs>
        <clipPath id="gw-g-clip">
          <path d="M7.25922 19.7927C7.25922 12.6759 13.0209 6.90668 20.1283 6.90668C27.2357 6.90668 32.9973 12.6759 32.9973 19.7927C32.9973 26.9094 27.2357 32.6786 20.1283 32.6786C13.0209 32.6786 7.25921 26.9094 7.25922 19.7927Z" />
        </clipPath>
        <GBlur id="gw-g-blur0" x={5.25922} y={4.90668} width={29.7381} height={29.772} />
        <GBlur id="gw-g-blur1" x={12.9977} y={14.828} width={14.1038} height={10.8265} />
        <GBlur id="gw-g-blur2" x={23.9146} y={13.1219} width={18.8784} height={10.1871} />
        <GBlur id="gw-g-blur3" x={15.8659} y={11.8415} width={18.8171} height={8.7561} />
        <GBlur id="gw-g-blur4" x={20.1341} y={8.54878} width={18.8171} height={8.7561} />
        <GBlur id="gw-g-blur5" x={13.9756} y={14.7683} width={21.0122} height={10.2195} />
        <GBlur id="gw-g-blur6" x={19.0404} y={9.00419} width={12.2878} height={10.0309} />
      </defs>
    </svg>
  );
}

One soft-blur filter per gradient layer, as exported in Google's asset.

function GBlur(props: {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
}): React.ReactElement {
  return (
    <filter
      id={props.id}
      x={props.x}
      y={props.y}
      width={props.width}
      height={props.height}
      filterUnits="userSpaceOnUse"
      colorInterpolationFilters="sRGB"
    >
      <feFlood floodOpacity={0} result="BackgroundImageFix" />
      <feBlend
        mode="normal"
        in="SourceGraphic"
        in2="BackgroundImageFix"
        result="shape"
      />
      <feGaussianBlur stdDeviation={1} result="effect1_foregroundBlur" />
    </filter>
  );
}
*/
